import { Directive, EventEmitter, HostListener, Output, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppState } from '@app/core/state/appState';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import { CountryType, isSweden } from 'src/app/core/entity/locale';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { getCorporateIdentityValidatorByCountry } from '../misc/corporate-number-validator';

const getValueLengthByCountry = (countryCode: CountryType) => {
  return isSweden(countryCode) ? 10 : 8;
};

@Directive({
  selector: '[pasteValidCorporateIdentity]',
  standalone: true,
})
export class PasteValidCorporateIdentityDirective {
  @Output() pasteError = new EventEmitter<void>();

  private corporateNumberValidator: Signal<{ valueLength: number; validatorFn: (value: string) => boolean }>;

  constructor(private store: Store<AppState>) {
    this.corporateNumberValidator = toSignal(
      this.store.select(FirmSelectors.selectCountryCode).pipe(
        map((countryCode) => ({
          valueLength: getValueLengthByCountry(countryCode),
          validatorFn: getCorporateIdentityValidatorByCountry(countryCode),
        })),
      ),
    );
  }

  @HostListener('paste', ['$event'])
  // eslint-disable-next-line complexity
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event?.clipboardData?.getData('text') ?? '';
    const pastedTextWithNumbersOnly = pastedText.replace(/\D/g, '');
    const { valueLength, validatorFn } = this.corporateNumberValidator();

    if (pastedTextWithNumbersOnly.length === valueLength && validatorFn(pastedTextWithNumbersOnly)) {
      this.insertText(pastedTextWithNumbersOnly);
      return;
    }

    const trimmedPastedText = pastedTextWithNumbersOnly.substring(pastedTextWithNumbersOnly.length - valueLength);
    if (validatorFn(trimmedPastedText)) {
      this.insertText(trimmedPastedText);
      return;
    }

    this.pasteError.emit();
  }

  private insertText(text: string) {
    document.execCommand('insertText', false, text);
  }
}
